import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { VehiclePictogram } from "@/components/PictogramList";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SECTION_TYPES = ["TEXTE", "RISQUES", "EPI", "PICTOGRAMMES", "CONSIGNES", "ANNEXE"];
const CADRE_DEFAULTS = {
  entreprise_accueil_nom: "", entreprise_accueil_coordonnees: "",
  entreprise_transport_nom: "", entreprise_transport_coordonnees: "",
  responsable_site_nom: "", responsable_site_fonction: "",
  lieu_operation: "", operation_repetitive: false,
  tonnage_volume: "", conditionnement: "",
  adr_classe: "", adr_code_danger: "", adr_numero_onu: "", adr_groupe_emballage: "",
  materiel_disposition: "", urgence_numero_site: "",
};

export default function CreateProtocol() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [types, setTypes] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [produits, setProduits] = useState([]);
  const [form, setForm] = useState({ titre: "", site_id: "", type_operation_id: "", description: "", date_echeance: "", type_vehicule_code: "", produit_id: "", ...CADRE_DEFAULTS });
  const [sections, setSections] = useState([{ titre_section: "", type_section: "TEXTE", contenu: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sites").then((res) => setSites(res.data.filter((s) => s.Actif)));
    api.get("/types-operations").then((res) => setTypes(res.data));
    api.get("/vehicules").then((res) => setVehicules(res.data));
    api.get("/produits").then((res) => setProduits(res.data));
  }, []);

  const updateSection = (idx, field, value) => {
    setSections(sections.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titre || !form.site_id || !form.type_operation_id) {
      toast.error(t("common.title") + " / " + t("common.site") + " / " + t("common.operation"));
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/protocoles", { ...form, sections });
      toast.success(res.data.Protocol_ID);
      navigate(`/protocole/${res.data.Protocol_ID}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const prefillFromReferentiel = async () => {
    const typeOp = types.find((o) => o.Type_ID === form.type_operation_id);
    if (!form.type_vehicule_code || !typeOp) {
      toast.error(t("common.select_vehicle_operation_first"));
      return;
    }
    const hasContent = sections.some((s) => s.titre_section.trim() || s.contenu.trim());
    if (hasContent && !window.confirm(t("common.prefill_overwrite_confirm"))) {
      return;
    }
    try {
      const res = await api.get("/referentiel/template", {
        params: { type_vehicule: form.type_vehicule_code, type_operation: typeOp.Code, produit_id: form.produit_id },
      });
      if (!res.data.nb_regles_applicables) {
        toast.error(t("common.no_applicable_rule"));
        return;
      }
      const newSections = [
        { titre_section: "Risques identifiés", type_section: "RISQUES", contenu: res.data.risques },
        { titre_section: "EPI requis", type_section: "EPI", contenu: res.data.epi },
        { titre_section: "Consignes de sécurité", type_section: "CONSIGNES", contenu: res.data.consignes },
      ].filter((s) => s.contenu);
      setSections(newSections.length ? newSections : sections);
      toast.success(t("common.prefill_success"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl space-y-6" data-testid="create-protocol-view">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{t("nav.create_protocol")}</h1>
      <form onSubmit={submit} className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 space-y-1.5">
            <Label>{t("common.title")}</Label>
            <Input data-testid="input-titre" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} required />
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.site")}</Label>
            <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
              <SelectTrigger data-testid="select-site">
                <SelectValue placeholder={t("common.site")} />
              </SelectTrigger>
              <SelectContent>
                {sites.map((s) => (
                  <SelectItem key={s.Site_ID} value={s.Site_ID}>
                    {s.Nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.operation")}</Label>
            <Select value={form.type_operation_id} onValueChange={(v) => setForm({ ...form, type_operation_id: v })}>
              <SelectTrigger data-testid="select-operation">
                <SelectValue placeholder={t("common.operation")} />
              </SelectTrigger>
              <SelectContent>
                {types.map((o) => (
                  <SelectItem key={o.Type_ID} value={o.Type_ID}>
                    {o.Nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.due_date")}</Label>
            <Input data-testid="input-echeance" type="date" value={form.date_echeance} onChange={(e) => setForm({ ...form, date_echeance: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.vehicule_type")}</Label>
            <Select value={form.type_vehicule_code} onValueChange={(v) => setForm({ ...form, type_vehicule_code: v })}>
              <SelectTrigger data-testid="select-vehicule">
                <SelectValue placeholder={t("common.vehicule_type")} />
              </SelectTrigger>
              <SelectContent>
                {vehicules.map((v) => (
                  <SelectItem key={v.Type_ID} value={v.Code}>
                    <span className="flex items-center gap-2">
                      <VehiclePictogram vehicule={v} size={22} />
                      {v.Nom}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.type_vehicule_code && (
              <div className="flex items-center gap-2 pt-1" data-testid="selected-vehicule-preview">
                <VehiclePictogram vehicule={vehicules.find((v) => v.Code === form.type_vehicule_code)} size={32} />
                <span className="text-xs text-slate-500">{vehicules.find((v) => v.Code === form.type_vehicule_code)?.Nom}</span>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.produit")}</Label>
            <Select value={form.produit_id} onValueChange={(v) => setForm({ ...form, produit_id: v })}>
              <SelectTrigger data-testid="select-produit">
                <SelectValue placeholder={t("common.produit")} />
              </SelectTrigger>
              <SelectContent>
                {produits.map((p) => (
                  <SelectItem key={p.Produit_ID} value={p.Produit_ID}>
                    {p.Designation}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label>{t("common.description")}</Label>
            <Textarea data-testid="input-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
          </div>
        </div>

        <div className="space-y-3" data-testid="cadre-protocole-block">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{t("cadre.title")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("cadre.entreprise_accueil_nom")}</Label>
              <Input data-testid="input-entreprise-accueil-nom" value={form.entreprise_accueil_nom} onChange={(e) => setForm({ ...form, entreprise_accueil_nom: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.coordonnees")}</Label>
              <Input data-testid="input-entreprise-accueil-coordonnees" value={form.entreprise_accueil_coordonnees} onChange={(e) => setForm({ ...form, entreprise_accueil_coordonnees: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.responsable_site_nom")}</Label>
              <Input data-testid="input-responsable-site-nom" value={form.responsable_site_nom} onChange={(e) => setForm({ ...form, responsable_site_nom: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.fonction")}</Label>
              <Input data-testid="input-responsable-site-fonction" value={form.responsable_site_fonction} onChange={(e) => setForm({ ...form, responsable_site_fonction: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.entreprise_transport_nom")}</Label>
              <Input data-testid="input-entreprise-transport-nom" value={form.entreprise_transport_nom} onChange={(e) => setForm({ ...form, entreprise_transport_nom: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.coordonnees")}</Label>
              <Input data-testid="input-entreprise-transport-coordonnees" value={form.entreprise_transport_coordonnees} onChange={(e) => setForm({ ...form, entreprise_transport_coordonnees: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.lieu_operation")}</Label>
              <Input data-testid="input-lieu-operation" value={form.lieu_operation} onChange={(e) => setForm({ ...form, lieu_operation: e.target.value })} />
            </div>
            <div className="flex items-center justify-between border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2">
              <Label>{t("cadre.operation_repetitive")}</Label>
              <Switch data-testid="switch-operation-repetitive" checked={form.operation_repetitive} onCheckedChange={(v) => setForm({ ...form, operation_repetitive: v })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.tonnage_volume")}</Label>
              <Input data-testid="input-tonnage-volume" value={form.tonnage_volume} onChange={(e) => setForm({ ...form, tonnage_volume: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.conditionnement")}</Label>
              <Input data-testid="input-conditionnement" value={form.conditionnement} onChange={(e) => setForm({ ...form, conditionnement: e.target.value })} placeholder="Vrac, Sac, Big-bag, Palette..." />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.adr_classe")}</Label>
              <Input data-testid="input-adr-classe" value={form.adr_classe} onChange={(e) => setForm({ ...form, adr_classe: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.adr_code_danger")}</Label>
              <Input data-testid="input-adr-code-danger" value={form.adr_code_danger} onChange={(e) => setForm({ ...form, adr_code_danger: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.adr_numero_onu")}</Label>
              <Input data-testid="input-adr-numero-onu" value={form.adr_numero_onu} onChange={(e) => setForm({ ...form, adr_numero_onu: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.adr_groupe_emballage")}</Label>
              <Input data-testid="input-adr-groupe-emballage" value={form.adr_groupe_emballage} onChange={(e) => setForm({ ...form, adr_groupe_emballage: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.materiel_disposition")}</Label>
              <Input data-testid="input-materiel-disposition" value={form.materiel_disposition} onChange={(e) => setForm({ ...form, materiel_disposition: e.target.value })} placeholder="Manche de chargement, Grue auxiliaire..." />
            </div>
            <div className="space-y-1.5">
              <Label>{t("cadre.urgence_numero_site")}</Label>
              <Input data-testid="input-urgence-numero-site" value={form.urgence_numero_site} onChange={(e) => setForm({ ...form, urgence_numero_site: e.target.value })} />
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">{t("common.sections")}</h2>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" data-testid="btn-prefill-referentiel" onClick={prefillFromReferentiel}>
                <Sparkles className="w-4 h-4" />
                {t("common.prefill_from_referentiel")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="btn-add-section"
                onClick={() => setSections([...sections, { titre_section: "", type_section: "TEXTE", contenu: "" }])}
              >
                <Plus className="w-4 h-4" />
                {t("common.add_section")}
              </Button>
            </div>
          </div>
          {sections.map((s, idx) => (
            <div key={idx} className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 space-y-3" data-testid={`section-block-${idx}`}>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  data-testid={`section-title-${idx}`}
                  placeholder={t("common.section_title")}
                  value={s.titre_section}
                  onChange={(e) => updateSection(idx, "titre_section", e.target.value)}
                />
                <Select value={s.type_section} onValueChange={(v) => updateSection(idx, "type_section", v)}>
                  <SelectTrigger data-testid={`section-type-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTION_TYPES.map((tp) => (
                      <SelectItem key={tp} value={tp}>
                        {tp}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                data-testid={`section-content-${idx}`}
                placeholder={t("common.section_content")}
                value={s.contenu}
                onChange={(e) => updateSection(idx, "contenu", e.target.value)}
                rows={3}
              />
              {sections.length > 1 && (
                <Button type="button" variant="ghost" size="sm" data-testid={`btn-remove-section-${idx}`} onClick={() => setSections(sections.filter((_, i) => i !== idx))}>
                  <Trash2 className="w-4 h-4" />
                  {t("common.remove")}
                </Button>
              )}
            </div>
          ))}
        </div>

        <Button type="submit" disabled={saving} data-testid="btn-submit-create-protocol">
          {t("common.create")}
        </Button>
      </form>
    </div>
  );
}
